// ── SpecificInvites — 3-friend personalised invite form ──────────────────────
// Used in OnboardingInterstitiel when feature_specific_invites is enabled.
// Each row collects {name, email/phone, channel} and produces a personalised
// mailto/sms link. Each click is logged in friend_invites_sent for K-factor metrics.

import { useState } from 'react';
import {
  logFriendInvite, buildPersonalInviteText,
  buildMailto, buildSmsLink,
} from '../utils/featurePackA';

export default function SpecificInvites({ link, inviterName, inviterId, lang = 'en', onAllInvited }) {
  const fr = lang === 'fr';
  const [rows, setRows] = useState([
    { name: '', contact: '', mode: 'email', sent: false },
    { name: '', contact: '', mode: 'email', sent: false },
    { name: '', contact: '', mode: 'email', sent: false },
  ]);

  const sentCount = rows.filter(r => r.sent).length;
  const setRow = (i, patch) => setRows(rs => rs.map((r, j) => (i === j ? { ...r, ...patch } : r)));

  const handleSend = async (i) => {
    const r = rows[i];
    if (!r.name?.trim() || !r.contact?.trim()) return;
    const body    = buildPersonalInviteText({ friendName: r.name, inviterName, link, lang });
    const subject = fr ? 'Aide-moi sur BeeLove ✦' : 'Help me on BeeLove ✦';
    const url = r.mode === 'sms'
      ? buildSmsLink({ phone: r.contact, body })
      : buildMailto({ friendEmail: r.contact, subject, body });
    window.open(url, '_blank');

    // Log non-blocking
    logFriendInvite({
      inviterId,
      friendName:  r.name,
      friendEmail: r.mode === 'email' ? r.contact : null,
      friendPhone: r.mode === 'sms'   ? r.contact : null,
      channel:     r.mode === 'email' ? 'mailto'  : 'sms',
      source:      'onboarding',
    });

    setRow(i, { sent: true });
    if (sentCount + 1 >= 3) onAllInvited?.();
  };

  return (
    <div style={{ marginBottom: 14 }}>
      <p style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#C9A84C', margin: '0 0 8px' }}>
        {fr ? '3 amis qui te connaissent bien' : '3 friends who know you well'}
      </p>
      <p style={{ fontSize: 11, color: '#888', margin: '0 0 12px', lineHeight: 1.5 }}>
        {fr
          ? 'Personnalise pour chacun — un message générique sera ignoré.'
          : 'Personalise each one — a generic message gets ignored.'}
      </p>

      {rows.map((r, i) => (
        <div key={i} style={{
          background: r.sent ? 'rgba(78,201,148,0.08)' : '#161616',
          border: `1px solid ${r.sent ? 'rgba(78,201,148,0.35)' : 'rgba(255,255,255,0.08)'}`,
          borderRadius: 8, padding: '10px 12px', marginBottom: 8,
          opacity: r.sent ? 0.7 : 1, transition: 'all 0.15s',
        }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <input
              value={r.name}
              onChange={e => setRow(i, { name: e.target.value })}
              placeholder={fr ? 'Prénom' : 'First name'}
              disabled={r.sent}
              style={{ flex: 1, padding: '6px 9px', background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: '#F2E8D0', fontSize: 12, fontFamily: 'inherit', outline: 'none' }}
            />
            <select
              value={r.mode}
              onChange={e => setRow(i, { mode: e.target.value })}
              disabled={r.sent}
              style={{ padding: '6px 6px', background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: '#F2E8D0', fontSize: 11, fontFamily: 'inherit', outline: 'none' }}>
              <option value="email">{fr ? 'email' : 'email'}</option>
              <option value="sms">SMS</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              value={r.contact}
              onChange={e => setRow(i, { contact: e.target.value })}
              placeholder={r.mode === 'email' ? 'email@example.com' : '+1 514…'}
              disabled={r.sent}
              type={r.mode === 'email' ? 'email' : 'tel'}
              style={{ flex: 1, padding: '6px 9px', background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: '#F2E8D0', fontSize: 12, fontFamily: 'inherit', outline: 'none' }}
            />
            <button
              onClick={() => handleSend(i)}
              disabled={r.sent || !r.name?.trim() || !r.contact?.trim()}
              style={{
                padding: '6px 12px',
                background: r.sent ? 'rgba(78,201,148,0.15)' : '#C9A84C',
                border: 'none', borderRadius: 6,
                color: r.sent ? '#4EC994' : '#0A0A0A',
                fontSize: 11, fontWeight: 700, cursor: r.sent ? 'default' : 'pointer',
                fontFamily: 'inherit',
                opacity: (!r.name?.trim() || !r.contact?.trim()) && !r.sent ? 0.4 : 1,
              }}>
              {r.sent ? '✓' : (fr ? 'Envoyer' : 'Send')}
            </button>
          </div>
        </div>
      ))}

      <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', textAlign: 'center', marginTop: 6 }}>
        {sentCount === 0
          ? (fr ? 'Aucun envoyé · ouvre ton client mail/SMS' : 'None sent · opens your mail/SMS app')
          : sentCount < 3
            ? (fr ? `${sentCount} sur 3 envoyés` : `${sentCount} of 3 sent`)
            : (fr ? '✓ 3 amis invités · radar prêt à grandir' : '✓ 3 friends invited · radar ready to grow')}
      </p>
    </div>
  );
}
